// src/screens/Fraud.js — Fraud & Abuse, console-standard redesign (June 2026).
// Same data + behaviour as before (admin_fraud_signals + admin_device_overlap,
// Users-console deep links via consoleBus), restyled onto ConsoleShell so it
// matches the rest of the admin consoles in both light and dark themes.

import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Empty, useConsoleTheme, F } from '../components/Console';
import { supabase } from '../supabaseClient';
import { setFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '…' : Number(n).toLocaleString());
const day = s => (s ? String(s).slice(0, 10) : '—');

function AccountsPill({ n, t }) {
  const over = n > 3;
  const c = over ? t.danger : t.warn;
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '1px 9px', borderRadius: 9, background: c + '22', color: c, whiteSpace: 'nowrap' }}>
      {n} accounts{over ? ' · over cap' : ''}
    </span>
  );
}

function Note({ t, children }) {
  return (
    <div style={{ background: t.rowbg, border: `1px solid ${t.bd}`, borderRadius: 9, padding: '10px 14px', fontSize: '0.85rem', color: t.mut, lineHeight: 1.55, marginTop: 14 }}>
      {children}
    </div>
  );
}

function FraudBody({ onNav }) {
  const { t } = useConsoleTheme();
  const [d, setD] = useState(null);
  const [dev, setDev] = useState(null);

  useEffect(() => {
    supabase.rpc('admin_fraud_signals').then(({ data }) => setD(data));
    supabase.rpc('admin_device_overlap').then(({ data }) => setDev(data));
  }, []);

  const openUser = (userId) => {
    if (!userId) return;
    setFocus({ type: 'user', value: userId });
    if (onNav) onNav('users');
  };

  const clusters = (dev && dev.clusters) || [];
  const th = { textAlign: 'left', padding: '7px 10px', color: t.mut, fontWeight: 500, fontSize: '0.8rem' };
  const thR = { ...th, textAlign: 'right' };
  const td = { padding: '7px 10px', fontSize: '0.85rem', color: t.tx };
  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: F, fontSize: '0.82rem', color: t.search, textAlign: 'left' };
  const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 10, marginBottom: 14 });

  return (
    <div>
      <div style={{ fontSize: '0.9rem', color: t.mut, margin: '2px 0 16px', lineHeight: 1.5 }}>
        Velocity, refund and rapid-purchase signals, plus device-fingerprint overlap. The purchase rule is 3 accounts
        per device — anything above is blocked at checkout and listed here.
      </div>

      <SectionLabel>Signals</SectionLabel>
      {!d ? <Empty>Loading signals…</Empty> : (
        <div style={grid(190)}>
          <KPI label='High-velocity users' value={fmt(d.high_velocity_users)} sub='>100 Sana queries / 24h' accent={t.danger} />
          <KPI label='Repeat refunders' value={fmt(d.repeat_refunders)} sub='2+ refunds lifetime' accent={t.warn} />
          <KPI label='Rapid purchases' value={fmt(d.rapid_purchase_under_5min)} sub='Pass within 5 min of sign-up' accent='#9F7E4F' />
        </div>
      )}

      <SectionLabel>Device fingerprints</SectionLabel>
      {!dev ? <Empty>Loading device overlap…</Empty> : (
        <div style={grid(170)}>
          <KPI label='Devices seen' value={fmt(dev.distinct_devices)} sub={fmt(dev.total_device_rows) + ' device-account links'} accent={t.accent} />
          <KPI label='Shared devices' value={fmt(dev.shared_devices)} sub='2+ accounts on one device' accent={t.warn} />
          <KPI label='Over-cap devices' value={fmt(dev.overcap_devices)} sub='>3 accounts — purchases blocked' accent={t.danger} />
          <KPI label='Multi-device users' value={fmt(dev.users_multi_device)} sub='one account on >3 devices' accent={t.mut} />
        </div>
      )}

      {dev && (
        <Panel title={`Shared-device clusters (${fmt(dev.shared_devices)})`} style={{ marginBottom: 14 }}>
          {clusters.length === 0 ? (
            <Empty>No device is linked to more than one account. The 3-accounts-per-device cap has no offenders.</Empty>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className='cc-tbl' style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${t.bd}` }}>
                    <th style={th}>Device</th>
                    <th style={th}>Platform / model</th>
                    <th style={th}>Accounts</th>
                    <th style={th}>Members</th>
                    <th style={thR}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {clusters.map(c => (
                    <tr key={c.fingerprint} style={{ borderBottom: `1px solid ${t.bd}`, verticalAlign: 'top' }}>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.74rem', color: t.accent }}>
                        {String(c.fingerprint).slice(0, 14)}…
                        {c.flagged && <span style={{ marginLeft: 6, fontSize: '0.66rem', fontWeight: 600, padding: '1px 7px', borderRadius: 9, background: t.danger + '22', color: t.danger }}>flagged</span>}
                      </td>
                      <td style={{ ...td, color: t.mut }}>{[c.platform, c.model].filter(Boolean).join(' · ') || '—'}</td>
                      <td style={td}><AccountsPill n={c.accounts} t={t} /></td>
                      <td style={td}>
                        {(c.members || []).map(m => (
                          <button key={m.user_id} onClick={() => openUser(m.user_id)}
                            title={'Open ' + (m.email || m.user_id) + ' in Users'}
                            style={{ ...linkBtn, display: 'block', padding: '1px 0' }}>
                            {m.email || ((m.user_id || '').slice(0, 8) + '…')} →
                          </button>
                        ))}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: t.mut }}>{day(c.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {d && (d.top_velocity || []).length > 0 && (
        <Panel title='Top high-velocity users' style={{ marginBottom: 14 }}>
          <table className='cc-tbl' style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${t.bd}` }}><th style={th}>User</th><th style={thR}>Queries / 24h</th></tr></thead>
            <tbody>
              {d.top_velocity.map(r => (
                <tr key={r.user_id} style={{ borderBottom: `1px solid ${t.bd}` }}>
                  <td style={td}>
                    <button onClick={() => openUser(r.user_id)} style={{ ...linkBtn, fontFamily: 'monospace', fontSize: '0.74rem' }}>{r.user_id} →</button>
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: t.danger, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.queries_24h)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {d && (d.repeat_refunder_list || []).length > 0 && (
        <Panel title='Repeat refunders' style={{ marginBottom: 14 }}>
          <table className='cc-tbl' style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${t.bd}` }}><th style={th}>User</th><th style={thR}>Refunds</th></tr></thead>
            <tbody>
              {d.repeat_refunder_list.map(r => (
                <tr key={r.user_id} style={{ borderBottom: `1px solid ${t.bd}` }}>
                  <td style={td}>
                    <button onClick={() => openUser(r.user_id)} style={{ ...linkBtn, fontFamily: 'monospace', fontSize: '0.74rem' }}>{r.user_id} →</button>
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: t.warn, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.refunds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Note t={t}>
        <strong style={{ color: t.tx }}>Policy.</strong> Devices over the 3-account cap are blocked from new purchases
        automatically. Refunds for accounts that appear in any cluster above should be denied per the locked refund
        policy — the refund endpoint enforces this server-side.
      </Note>
    </div>
  );
}

export default function Fraud(props) {
  if (props && props.embed) return <FraudBody {...props} />;
  return (
    <ConsoleShell title='Fraud & Abuse'>
      <FraudBody {...props} />
    </ConsoleShell>
  );
}
